import React, { PureComponent } from 'react';
import cx from 'classnames';
import _ from 'lodash';

type OptionProps = React.OptionHTMLAttributes<HTMLOptionElement>;

/** [value, label?, group?, optionProps?] — kept loose because options come from many
 *  heterogeneous sources (operation menus, static tuple lists, etc.). */
export type SelectOption = ReadonlyArray<unknown>;

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'children'> {
    value?: unknown;
    options: SelectOption[];
    readOnly?: boolean;
    children?: React.ReactElement;
    renderLabel?: (label: React.ReactNode, value: unknown) => React.ReactNode;
    rootProps?: Record<string, unknown>;
}

export default class Select extends PureComponent<SelectProps> {
    static defaultProps = {
        renderLabel: (label: React.ReactNode, _value: unknown) => label,
    };

    renderGroups(options: SelectOption[]) {
        const groupMap: Record<string, SelectOption[]> = {};
        for (let [key, name, group, props] of options) {
            const groupKey = group != null ? String(group) : 'null';
            groupMap[groupKey] = groupMap[groupKey] || [];
            groupMap[groupKey].push([key, name, null, props]);
        }

        return <React.Fragment>
            {groupMap['null'] && this.renderOptions(groupMap['null'])}
            {Object.keys(_.omit(groupMap, 'null')).map(label =>
                <optgroup key={label} label={label}>
                    {this.renderOptions(groupMap[label])}
                </optgroup>,
            )}
        </React.Fragment>;
    }

    renderOptions(options: SelectOption[]) {
        return options.map(([val, label, _group, props]) =>
            <option
                {...(props as OptionProps)}
                key={String(val)}
                value={val as string | number | undefined}
            >{(label as React.ReactNode) || String(val)}</option>,
        );
    }

    render() {
        const { options, value, readOnly, children, renderLabel, className, rootProps, ...props } = this.props;

        let label: React.ReactNode = '[no value]';
        const obj = options.find(([val]) => val === value);
        if (obj) label = (obj[1] as React.ReactNode) || String(obj[0]);
        else if (value) {
            label = <span style={{ color: 'red' }}>[invalid value: {String(value)}]</span>;
        }

        let childOptions: React.ReactNode;
        if (options.some(item => item[2])) childOptions = this.renderGroups(options);
        else childOptions = this.renderOptions(options);

        return <div {...(rootProps as React.HTMLAttributes<HTMLDivElement>)} className={cx('select', readOnly && 'select-readOnly', className)}>
            {children ? children :
                <div className='select-handle'>{renderLabel!(label, value)}</div>
            }
            {readOnly ? null :
                <select value={value == null ? '' : value as string | number | readonly string[]} {...props} tabIndex={-1}>
                    {value != null ? null : <option disabled value=''>[no value]</option>}
                    {childOptions}
                </select>
            }
        </div>;
    }
}
